import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Drawer, Skeleton } from "@mui/material";
import {
  Scale as ScaleIcon,
  MonitorHeart as MonitorHeartIcon,
  DeviceThermostat as DeviceThermostatIcon,
  WaterDrop as WaterDropIcon,
  Favorite as FavoriteIcon,
  Analytics as AnalyticsIcon,
  ListAlt as ListAltIcon,
  Straighten as TapeIcon,
} from "@mui/icons-material";
import ActionButton from "../../atoms/ActionButton";
import { useHasPermission } from "../../../hooks/useHasPermission";
import ManualMeasurementDrawer from "./drawers/ManualMeasurementDrawer";
import BodyMeasurementsSummary from "../BodyMeasurementsSummary";
import SectionCard from "../../molecules/SectionCard";
import EmptyState from "../../atoms/EmptyState";
import { InfoRow, DrawerHeader, SegmentedControl } from "../../atoms";
import MetricTrendChart from "../charts/MetricTrendChart";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, dashboardSectionSx } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import { relativePast } from "../../../pages/Secretary/PatientsPage/patientListFormat";
import { measurementStatus, NORMAL_RANGES, STATUS_META } from "./measurementRanges";
import { MetricCard, MetricCardSkeleton, StatRow, StatusPill } from "./_measurementParts";
import { useListPatientMeasurementsMutation } from "../../../store/secretaryApi";
import { formatDateTime } from "./deviceMeta";
import BodyCompositionReport from "../BodyCompositionReport";
import { profileFromUser } from "../../../utils/bodyReport";
import useTerms from "../../../hooks/useTerms";

// Kategori → görsel/etiket eşlemesi. Değer alanları BURADA sabitlenmez —
// her kategori kendi kayıtlarında GERÇEKTE bulunan `values[].key` alanlarına
// göre dinamik render edilir (ör. SpO2 ölçümü nabız/perfüzyon indeksini de
// içeriyorsa otomatik gösterilir, glukometrenin tek alanı varsa tek grafik çizilir).
const METRIC_META = {
  weight: { label: "Kilo", icon: ScaleIcon },
  blood_pressure: { label: "Tansiyon", icon: MonitorHeartIcon },
  spo2: { label: "SpO₂ (Oksijen)", icon: FavoriteIcon },
  body_temperature: { label: "Vücut Sıcaklığı", icon: DeviceThermostatIcon },
  blood_glucose: { label: "Kan Şekeri", icon: WaterDropIcon },
  body_circumference: { label: "Mezura", icon: TapeIcon },
};
const FALLBACK_META = { label: "Ölçüm", icon: AnalyticsIcon };

// Bağımsız bir kategori olarak GÖSTERİLMEZ: "heart_rate" eski sözleşmenin
// kalıntısı (nabız artık ilgili ölçüme gömülü geliyor, ayrı bir cihaz ölçümü
// değil). Bu tipe ait kayıtlar varsa bile seçici listede görünmez.
const EXCLUDED_CATEGORIES = new Set(["heart_rate"]);

// "body_composition" ayrı bir kategori DEĞİL — akıllı tartının tek bir
// tartılma olayında ürettiği ek veri (BMI, yağ/kas oranı vb.), bu yüzden
// "Kilo" kategorisiyle aynı kovaya (bucket) toplanır: seçici listede tek
// "Kilo" kartı görünür ama detayında tartı + vücut analizi alanlarının
// TÜMÜ (veride gerçekten bulunanlar) birlikte gösterilir.
const CATEGORY_MERGE = { body_composition: "weight" };
function bucketFor(metricType) {
  return CATEGORY_MERGE[metricType] || metricType;
}

// Değer alanı anahtarı → Türkçe etiket. Birim veriden gelir (values[].unit),
// burada sadece isimlendirme tutulur; bilinmeyen bir anahtar ham haliyle gösterilir.
const VALUE_LABELS = {
  weight: "Kilo",
  heartRate: "Nabız",
  systolic: "Sistolik",
  diastolic: "Diastolik",
  spo2: "SpO₂",
  pi: "Perfüzyon İndeksi",
  temperature: "Sıcaklık",
  glucose: "Şeker",
  // Mezura ölçüleri (elle girilir)
  waist: "Bel",
  hip: "Kalça",
  chest: "Göğüs",
  arm: "Kol",
  thigh: "Bacak",
  neck: "Boyun",
  // Vücut analizi (akıllı tartı) — Kilo kategorisine gömülü gelir.
  bmi: "BMI",
  bodyFatPct: "Yağ Oranı",
  bodyFatKg: "Yağ Kütlesi",
  waterPct: "Su Oranı",
  muscleKg: "Kas Kütlesi",
  musclePct: "Kas Oranı",
  boneKg: "Kemik Kütlesi",
  bmr: "Bazal Metabolizma",
  visceralFat: "Visseral Yağ",
  proteinPct: "Protein Oranı",
  bodyAge: "Vücut Yaşı",
  idealWeightKg: "İdeal Kilo",
  bodyScore: "Vücut Skoru",
  skeletalMusclePct: "İskelet Kası Oranı",
  subcutaneousFatPct: "Deri Altı Yağ Oranı",
  leanBodyMassKg: "Yağsız Vücut Kütlesi",
  obesityPct: "Obezite Oranı",
  skeletalMuscleKg: "İskelet Kası Kütlesi",
  subcutaneousFatKg: "Deri Altı Yağ Kütlesi",
  proteinKg: "Protein Kütlesi",
  mineralKg: "Mineral Kütlesi",
  cellMassKg: "Hücre Kütlesi",
  waterKg: "Su Kütlesi",
  waterECWKg: "Hücre Dışı Su",
  waterICWKg: "Hücre İçi Su",
  smi: "İskelet Kası İndeksi",
  whr: "Bel-Kalça Oranı",
  dci: "Günlük Kalori İhtiyacı",
  standardWeightKg: "Standart Kilo",
  controlWeightKg: "Kilo Kontrol Farkı",
  fatControlKg: "Yağ Kontrol Farkı",
  muscleControlKg: "Kas Kontrol Farkı",
  fatKgLeftArm: "Sol Kol Yağ Kütlesi",
  fatKgRightArm: "Sağ Kol Yağ Kütlesi",
  fatKgLeftLeg: "Sol Bacak Yağ Kütlesi",
  fatKgRightLeg: "Sağ Bacak Yağ Kütlesi",
  fatKgTrunk: "Gövde Yağ Kütlesi",
  fatRateLeftArm: "Sol Kol Yağ Oranı",
  fatRateRightArm: "Sağ Kol Yağ Oranı",
  fatRateLeftLeg: "Sol Bacak Yağ Oranı",
  fatRateRightLeg: "Sağ Bacak Yağ Oranı",
  fatRateTrunk: "Gövde Yağ Oranı",
  muscleKgLeftArm: "Sol Kol Kas Kütlesi",
  muscleKgRightArm: "Sağ Kol Kas Kütlesi",
  muscleKgLeftLeg: "Sol Bacak Kas Kütlesi",
  muscleKgRightLeg: "Sağ Bacak Kas Kütlesi",
  muscleKgTrunk: "Gövde Kas Kütlesi",
  muscleRateLeftArm: "Sol Kol Kas Oranı",
  muscleRateRightArm: "Sağ Kol Kas Oranı",
  muscleRateLeftLeg: "Sol Bacak Kas Oranı",
  muscleRateRightLeg: "Sağ Bacak Kas Oranı",
  muscleRateTrunk: "Gövde Kas Oranı",
};

// Bu kategorilerde tüm alanlar (ör. Kilo + gömülü vücut analizi — 10+ alan
// olabilir) grafikte/tabloda değil, "Tüm Detaylar" butonuyla açılan bir
// drawer'da gösterilir: grafik sade kalır (sadece ana değer), tablo hiç
// render edilmez.
const DETAIL_DRAWER_CATEGORIES = new Set(["weight"]);

// Kategori kartında/özet istatistiklerde "ana" değer olarak öne çıkarılacak
// alan — o kategoriye özgü kayıtlarda bu alan yoksa ilk bulunan alana düşülür.
const PRIMARY_KEY = {
  weight: "weight",
  blood_pressure: "systolic",
  spo2: "spo2",
  body_temperature: "temperature",
  blood_glucose: "glucose",
  body_circumference: "waist",
};

function metaFor(metricType) {
  return METRIC_META[metricType] || { ...FALLBACK_META, label: metricType };
}

function labelForKey(key) {
  return VALUE_LABELS[key] || key;
}

function valueOf(values, key) {
  const v = (values || []).find((x) => x.key === key);
  return v ? v.value : null;
}

function primaryKeyFor(metricType, availableKeys) {
  const preferred = PRIMARY_KEY[metricType];
  if (preferred && availableKeys.some((k) => k.key === preferred)) return preferred;
  return availableKeys[0]?.key || null;
}

// Birleşik kovada (ör. Kilo + Vücut Analizi) tıklanan nokta TEK bir kaynak
// tipin kaydıdır (weight VE body_composition ayrı Measurement dokümanları).
// Drawer'da tüm alanları gösterebilmek için zaman damgası en yakın olan
// diğer tip kaydıyla birleştiriyoruz — yoksa tıklanan kayıtta olmayan
// alanlar (ör. weight kaydında BMI) boş görünür.
function mergeWithNearestCompanion(clicked, allRecords) {
  const companions = allRecords.filter((r) => r.metricType !== clicked.metricType);
  if (companions.length === 0) return clicked;
  const clickedTime = new Date(clicked.takenAt).getTime();
  const nearest = companions.reduce((best, r) => {
    const d = Math.abs(new Date(r.takenAt).getTime() - clickedTime);
    return !best || d < best.d ? { r, d } : best;
  }, null);
  if (!nearest) return clicked;
  // 3 dakikalık pencere: mobildeki aynı tartılma eşleşmesiyle (nearestByTime,
  // windowMs = 3*60*1000) hizalı — bu aralığın dışındaki en yakın kayıt bile
  // farklı bir tartılma olayına ait sayılır, birleştirilmez.
  if (nearest.d > 3 * 60 * 1000) return clicked;
  return {
    takenAt: clicked.takenAt,
    values: [...(clicked.values || []), ...(nearest.r.values || [])],
  };
}

function round(n, digits = 1) {
  if (n == null || Number.isNaN(n)) return null;
  const p = 10 ** digits;
  return Math.round(n * p) / p;
}

/**
 * Bir dizi geçmiş kayıttan (eskiden-yeniye sıralı) tek bir alan için özet
 * çıkarır: ortalama/min/max ve ilk-son karşılaştırması (trend).
 */
function summarizePrimaryValue(records, primaryKey) {
  if (!primaryKey) return null;
  const vals = records.map((r) => valueOf(r.values, primaryKey)).filter((v) => v != null);
  if (vals.length === 0) return null;
  const avg = round(vals.reduce((a, b) => a + b, 0) / vals.length);
  const min = round(Math.min(...vals));
  const max = round(Math.max(...vals));
  const first = vals[0];
  const last = vals[vals.length - 1];
  const delta = round(last - first);
  const direction = delta == null || Math.abs(delta) < 0.05 ? "flat" : delta > 0 ? "up" : "down";
  return { count: vals.length, avg, min, max, first, last, delta, direction };
}

const fmtNum = (n) =>
  n == null || Number.isNaN(n) ? "—" : Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 1 });
const fmtDay = (d) => new Date(d).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });

// Tansiyon tek değer gibi okunur: "132/85"
const isBp = (metricType) => metricType === "blood_pressure";
const displayValue = (metricType, values, primaryKey) =>
  isBp(metricType)
    ? `${fmtNum(valueOf(values, "systolic"))}/${fmtNum(valueOf(values, "diastolic"))}`
    : fmtNum(valueOf(values, primaryKey));

const PERIODS = [
  { value: "30", label: "30 gün" },
  { value: "90", label: "90 gün" },
  { value: "all", label: "Tümü" },
];

/** Düz Türkçe yorum: ortalama, yön ve sınır dışı ölçümler. */
function buildInsight(meta, summary, unit, outOfRange) {
  if (!summary) return null;
  if (summary.count < 2) return `Bu dönemde tek ${meta.label.toLocaleLowerCase("tr")} ölçümü var; eğilim için en az iki ölçüm gerekir.`;
  const dir =
    summary.direction === "flat"
      ? "belirgin bir değişim yok"
      : `ilk ölçüme göre ${fmtNum(Math.abs(summary.delta))} ${unit} ${summary.direction === "up" ? "arttı" : "azaldı"}`;
  const range =
    outOfRange == null
      ? ""
      : outOfRange === 0
        ? " Tüm ölçümler normal aralıkta."
        : ` ${outOfRange} ölçüm normal aralığın dışında.`;
  return `${summary.count} ölçümün ortalaması ${fmtNum(summary.avg)} ${unit}; ${dir}.${range}`;
}

// Detay kartının grafik alanı sabit yükseklikte (design-principles §4)
const TREND_BODY_HEIGHT = 340;

/**
 * DeviceMeasurementsTab — hastanın BLE ölçüm cihazlarından gelen ölçümler
 * (salt-okunur). Üstte her ölçüm türü için kart (son değer + durum + mini
 * eğilim); altta seçili türün detayı: normal aralık bantlı grafik + özet
 * sütunu + düz Türkçe yorum. Kilo noktasına tıklanınca vücut analizi
 * drawer'da açılır. Eşikler: measurementRanges.js (sunucuyla elle senkron).
 *
 * @property {"standalone" | "session"} mode
 * @property {string} patientId
 * @property {string} [clinicId]
 * @property {string} token
 * @property {object} [patient] — rapor profili (boy/doğum tarihi/cinsiyet) için hasta dokümanı
 */
export default function DeviceMeasurementsTab({ mode, patientId, clinicId, token, patient }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [listMeasurements] = useListPatientMeasurementsMutation();
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [period, setPeriod] = useState("90");
  const [drawerRecord, setDrawerRecord] = useState(null);
  const canCreateManual = useHasPermission("measurements:create");
  const [manualOpen, setManualOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (mode !== "standalone" || !token || !patientId) return;
    let active = true;
    (async () => {
      try {
        const res = await listMeasurements({ token, patientId, clinicId }).unwrap();
        if (active) setList(res?.data?.list || []);
      } catch (_e) {
        if (active) setList([]);
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, token, patientId, clinicId, reloadKey]);

  const manualAction =
    canCreateManual && mode === "standalone" ? (
      <ActionButton variant="add" compact palette={palette} label="Elle ölçüm gir" onClick={() => setManualOpen(true)} />
    ) : null;
  const manualDrawer = (
    <ManualMeasurementDrawer
      open={manualOpen}
      onClose={() => setManualOpen(false)}
      patientId={patientId}
      token={token}
      onSaved={() => setReloadKey((k) => k + 1)}
    />
  );

  // Kova → kayıtlar (eskiden yeniye)
  const byMetricType = useMemo(() => {
    const map = new Map();
    list.forEach((m) => {
      const bucket = bucketFor(m.metricType);
      if (!map.has(bucket)) map.set(bucket, []);
      map.get(bucket).push(m);
    });
    map.forEach((arr) => arr.sort((a, b) => new Date(a.takenAt) - new Date(b.takenAt)));
    return map;
  }, [list]);

  const categories = useMemo(
    () => Array.from(byMetricType.keys()).filter((t) => !EXCLUDED_CATEGORIES.has(t)),
    [byMetricType]
  );

  // Seçim yoksa/geçersizse ilk tür — efektle değil doğrudan (ilk karede boş görünmesin)
  const activeCat = selected && categories.includes(selected) ? selected : categories[0] || null;

  // Kategori başına: değer alanları, ana alan, son kayıt, durum, mini eğilim
  const cards = useMemo(
    () =>
      categories.map((cat) => {
        const records = byMetricType.get(cat) || [];
        const keys = new Map();
        records.forEach((r) => (r.values || []).forEach((v) => !keys.has(v.key) && keys.set(v.key, v.unit)));
        const valueKeys = Array.from(keys.entries()).map(([key, unit]) => ({ key, unit }));
        const primaryKey = primaryKeyFor(cat, valueKeys);
        const withPrimary = records.filter((r) => valueOf(r.values, primaryKey) != null);
        const last = withPrimary[withPrimary.length - 1] || null;
        return {
          cat,
          meta: metaFor(cat),
          valueKeys,
          primaryKey,
          unit: valueKeys.find((v) => v.key === primaryKey)?.unit || "",
          last,
          count: withPrimary.length,
          status: last ? measurementStatus(last.metricType, last.values) : null,
          trend: withPrimary.slice(-12).map((r) => valueOf(r.values, primaryKey)),
        };
      }),
    [categories, byMetricType]
  );
  const card = cards.find((c) => c.cat === activeCat) || null;

  // Seçili dönem
  const periodRecords = useMemo(() => {
    const all = card ? byMetricType.get(card.cat) || [] : [];
    if (period === "all") return all;
    const from = Date.now() - Number(period) * 24 * 60 * 60 * 1000;
    return all.filter((r) => new Date(r.takenAt).getTime() >= from);
  }, [card, byMetricType, period]);

  const loading = mode === "standalone" && !loaded;

  if (!loading && categories.length === 0) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, pb: 6 }}>
        <EmptyState
          variant="illustrated"
          icon={<AnalyticsIcon />}
          title="Henüz ölçüm kaydı yok"
          description={
            canCreateManual
              ? `${terms.patient} bağlı bir ölçüm cihazıyla ölçüm aldığında değerler burada görünür; kilo ve mezura ölçülerini elle de girebilirsiniz.`
              : `${terms.patient} bağlı bir ölçüm cihazıyla ölçüm aldığında kilo, tansiyon, oksijen gibi değerler burada görünür.`
          }
        />
        {manualAction}
        {manualDrawer}
      </Box>
    );
  }

  // ── Seçili tür detayı ─────────────────────────────────────────────
  const primaryKey = card?.primaryKey;
  const unit = card?.unit || "";
  const useDetailDrawer = card ? DETAIL_DRAWER_CATEGORIES.has(card.cat) : false;
  const range = card ? NORMAL_RANGES[card.cat] : null;
  const bp = card ? isBp(card.cat) : false;

  const primaryRecords = periodRecords.filter((r) => valueOf(r.values, primaryKey) != null);
  const summary = summarizePrimaryValue(primaryRecords, primaryKey);
  const diaSummary = bp ? summarizePrimaryValue(primaryRecords, "diastolic") : null;
  const statuses = primaryRecords.map((r) => measurementStatus(r.metricType, r.values));
  const outOfRange = range ? statuses.filter((st) => st && st !== "normal").length : null;
  const insight = card ? buildInsight(card.meta, summary, unit, outOfRange) : null;
  const pair = (a, b) => (bp ? `${fmtNum(a)}/${fmtNum(b)}` : fmtNum(a));

  // Grafik: kilo gibi drawer'lı türde yalnız ana değer; diğerlerinde tüm alanlar
  const chartValueKeys = !card
    ? []
    : useDetailDrawer
      ? card.valueKeys.filter((v) => v.key === primaryKey)
      : card.valueKeys;
  const chartData = periodRecords
    .filter((r) => chartValueKeys.some((vk) => valueOf(r.values, vk.key) != null))
    .map((r) => {
      const point = { takenAt: r.takenAt, _record: r };
      chartValueKeys.forEach((vk) => {
        point[vk.key] = valueOf(r.values, vk.key);
      });
      return point;
    });

  const drawerHasComposition = (drawerRecord?.values || []).some(
    (v) => v.key !== "weight" && v.key !== "heartRate"
  );
  const ActiveIcon = card?.meta.icon || AnalyticsIcon;
  const subtitle =
    primaryRecords.length > 0
      ? `${primaryRecords.length} ölçüm · ${fmtDay(primaryRecords[0].takenAt)} – ${fmtDay(primaryRecords[primaryRecords.length - 1].takenAt)}`
      : "Bu dönemde ölçüm yok";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      {manualAction && <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{manualAction}</Box>}
      {manualDrawer}
      {/* ── Ölçüm türleri ── */}
      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
        {loading
          ? [0, 1, 2].map((i) => <MetricCardSkeleton key={i} />)
          : cards.map((c) => (
              <MetricCard
                key={c.cat}
                icon={c.meta.icon}
                label={c.meta.label}
                value={c.last ? displayValue(c.cat, c.last.values, c.primaryKey) : "—"}
                unit={c.unit}
                status={c.status}
                trend={c.trend}
                meta={c.last ? `${relativePast(c.last.takenAt)} · ${c.count} ölçüm` : "Ölçüm yok"}
                active={c.cat === activeCat}
                onClick={() => setSelected(c.cat)}
                palette={palette}
              />
            ))}
      </Box>

      {/* ── Vücut ölçüleri yorumu (yalnız Mezura seçiliyken) ── */}
      {activeCat === "body_circumference" && (
        <SectionCard
          icon={<TapeIcon sx={{ fontSize: 18 }} />}
          title="Vücut ölçüleri"
          subtitle="Son ölçüler, ilk ölçüme göre değişim ve risk göstergeleri"
          sx={dashboardSectionSx}
        >
          <Box sx={{ p: CARD_PAD }}>
            <BodyMeasurementsSummary records={list} patient={patient} palette={palette} />
          </Box>
        </SectionCard>
      )}

      {/* ── Seçili türün detayı ── */}
      <SectionCard
        icon={<ActiveIcon sx={{ fontSize: 18 }} />}
        title={loading || !card ? "Ölçüm geçmişi" : card.meta.label}
        subtitle={loading ? undefined : subtitle}
        action={
          <SegmentedControl value={period} onChange={setPeriod} options={PERIODS} ariaLabel="Dönem" isLoading={loading} />
        }
        sx={{ ...dashboardSectionSx, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 300px" } }}>
          {/* Grafik */}
          <Box sx={{ p: CARD_PAD, height: TREND_BODY_HEIGHT, display: "flex", flexDirection: "column", minHeight: 0 }}>
            {loading ? (
              <Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 2 }} />
            ) : chartData.length === 0 ? (
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <EmptyState
                  variant="illustrated-sm"
                  icon={<ActiveIcon />}
                  title="Bu dönemde ölçüm yok"
                  description="Daha uzun bir dönem seçin."
                  sx={{ py: 0 }}
                />
              </Box>
            ) : (
              <>
                <MetricTrendChart
                  data={chartData}
                  valueKeys={chartValueKeys.map((k) => ({ key: k.key, label: labelForKey(k.key), unit: k.unit }))}
                  band={range?.band}
                  lines={range?.lines}
                  pointColor={(pt) => {
                    const st = measurementStatus(pt._record?.metricType, pt._record?.values);
                    return st && st !== "normal" ? STATUS_META[st].color : null;
                  }}
                  onPointClick={
                    useDetailDrawer
                      ? (point) => setDrawerRecord(mergeWithNearestCompanion(point._record, byMetricType.get(card.cat) || []))
                      : undefined
                  }
                />
                {useDetailDrawer || range ? (
                  <Typography sx={{ mt: 1, fontSize: TYPO.caption, color: COLORS.slate500 }}>
                    {[
                      range?.band ? "Yeşil alan normal aralık" : null,
                      range ? "renkli noktalar aralık dışı ölçüm" : null,
                      useDetailDrawer ? "vücut analizi için bir noktaya tıklayın" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                      .replace(/^./, (c) => c.toLocaleUpperCase("tr"))}
                  </Typography>
                ) : null}
              </>
            )}
          </Box>

          {/* Özet */}
          <Box
            sx={{
              px: CARD_PAD,
              py: 1,
              borderLeft: { lg: `1px solid ${COLORS.slate100}` },
              borderTop: { xs: `1px solid ${COLORS.slate100}`, lg: "none" },
            }}
          >
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <Box key={i} sx={{ py: 1.25, display: "flex", justifyContent: "space-between" }}>
                  <Skeleton width={90} />
                  <Skeleton width={70} />
                </Box>
              ))
            ) : summary ? (
              <>
                <StatRow
                  label="Son ölçüm"
                  hint={relativePast(primaryRecords[primaryRecords.length - 1].takenAt)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, justifyContent: "flex-end" }}>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
                      {pair(summary.last, diaSummary?.last)} <Box component="span" sx={{ fontWeight: 500, color: COLORS.slate500, fontSize: TYPO.label }}>{unit}</Box>
                    </Typography>
                    <StatusPill status={statuses[statuses.length - 1]} />
                  </Box>
                </StatRow>
                <StatRow label="Ortalama" value={`${pair(summary.avg, diaSummary?.avg)} ${unit}`} />
                <StatRow label="En düşük" value={`${pair(summary.min, diaSummary?.min)} ${unit}`} />
                <StatRow label="En yüksek" value={`${pair(summary.max, diaSummary?.max)} ${unit}`} />
                {range ? (
                  <StatRow label="Aralık dışı" hint={`Normal: ${range.text}`}>
                    <Typography
                      sx={{
                        fontSize: TYPO.body,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                        color: outOfRange ? COLORS.errorDark : COLORS.successDark,
                      }}
                    >
                      {`${outOfRange} / ${summary.count} ölçüm`}
                    </Typography>
                  </StatRow>
                ) : null}
                {insight ? (
                  <Typography sx={{ pt: 1.5, pb: 1, fontSize: TYPO.label, lineHeight: 1.55, color: COLORS.slate600 }}>
                    {insight}
                  </Typography>
                ) : null}
              </>
            ) : (
              <Typography sx={{ py: 2, fontSize: TYPO.label, color: COLORS.slate500 }}>
                Bu dönemde özet çıkarılacak ölçüm yok.
              </Typography>
            )}
          </Box>
        </Box>
      </SectionCard>

      {/* ── Tüm Detaylar — grafikte tıklanan noktanın tam dökümü ── */}
      <Drawer
        anchor="right"
        open={Boolean(drawerRecord)}
        onClose={() => setDrawerRecord(null)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, bgcolor: COLORS.white, boxShadow: "-8px 0 30px rgba(15,23,42,0.08)" } }}
      >
        <DrawerHeader
          icon={<ListAltIcon />}
          title={`${card?.meta.label || "Ölçüm"} — Tüm Detaylar`}
          subtitle={drawerRecord ? formatDateTime(drawerRecord.takenAt) : undefined}
          onClose={() => setDrawerRecord(null)}
        />
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {drawerRecord && card?.cat === "weight" && drawerHasComposition ? (
            <BodyCompositionReport values={drawerRecord.values} profile={profileFromUser(patient)} />
          ) : (
            (card?.valueKeys || []).map((vk) => {
              const v = drawerRecord ? valueOf(drawerRecord.values, vk.key) : null;
              return (
                <InfoRow key={vk.key} label={labelForKey(vk.key)}>
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate700 }}>
                    {v != null ? `${fmtNum(v)} ${vk.unit}` : "—"}
                  </Typography>
                </InfoRow>
              );
            })
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
